import { ContentCopy } from '@mui/icons-material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Alert } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';
import makeStyles from '@mui/styles/makeStyles';
import Bowser from 'bowser';
import React from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/auth-context';
import { usePermissions } from '../../hooks/use-permissions';
import { AWS_GROUP } from '../../types';
import { IConnectSignIn } from './integrity-connect-sign-in';
import { NavDataLink } from './nav-data';

const useStyles = makeStyles({
  listItemText: {
    marginLeft: 10,
    marginRight: 10,
  },
});

const detectMobile = (): boolean => {
  const parser = Bowser.getParser(window.navigator.userAgent);
  return parser.getPlatform().type === 'mobile';
};

// Material-UI needs to be able to Forward the Ref to the List Items when they are located in another file
// https://material-ui.com/guides/composition/#caveat-with-refs
export const NavSectionLink = React.forwardRef(
  (props: { item: NavDataLink }, ref: React.Ref<any> | undefined) => {
    // Props
    const { item } = props;
    // Context
    const { state } = React.useContext(AuthContext);
    // State
    const [show, setShow] = React.useState<boolean>(false);
    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    // Hooks
    const classes = useStyles();
    const { checkPermissions, checkBillingAccess, hasGroup } = usePermissions();

    // Without stopping thr propagation of the 'tab'
    // the menu will close instead of focusing on the next link
    const stopPropagationForTab = (
      event: React.KeyboardEvent<HTMLAnchorElement | HTMLDivElement>,
    ) => {
      if (event.key === 'Tab') {
        event.stopPropagation();
      }
    };

    let canView = true;
    if (item.hasGroup && item.hasGroup === AWS_GROUP.ARC_BillingSystem) {
      // Check that the Billing User has the necessary group and some permissions have been set
      const hasPermissions: boolean = Boolean(state.billingPermissions.length);
      canView = hasGroup(item.hasGroup) && hasPermissions;
    } else if (item.hasGroup) {
      // User must have this specific group to view link
      canView = hasGroup(item.hasGroup);
    } else if (item.permissions) {
      // User must have one of the permissions
      canView = checkPermissions(item.permissions);
    }

    let hideFromBilling = false;
    if (item.name && item.access) {
      // Only Billing Navigation items with name and access will be hidden and checked
      hideFromBilling = !checkBillingAccess(item.name, item.access);
    }

    let hideFromStaff = false;
    // Check if user is an agent staff account
    const staffAgtNo = state.session?.getAccessToken().decodePayload()[
      'myStaffForAgt'
    ];
    // Used for Registration with prefilled recruiter
    const RecAgtNo = staffAgtNo || state.user.username;

    // Check which pages are permitted for agent staff
    const staffPerms =
      state.session?.getAccessToken().decodePayload()['cognito:groups'] || [];
    if (staffAgtNo && item.staffPermission) {
      hideFromStaff = !staffPerms.includes(item.staffPermission);
    }

    if (!hideFromBilling && item.children && canView && !hideFromStaff) {
      return (
        <React.Fragment>
          <ListItem
            ref={ref}
            dense
            button
            onClick={() => setShow((currentState) => !currentState)}
            onKeyDown={stopPropagationForTab}
          >
            {item.startIcon || null}
            <ListItemText
              primary={item.title}
              className={classes.listItemText}
            />
            {show ? <ExpandLess /> : <ExpandMore />}
          </ListItem>

          <Collapse in={show} timeout="auto" unmountOnExit>
            <List dense component="div" disablePadding>
              {item.children.map((link) => {
                if (
                  item.title === 'Integrity Connect' &&
                  link.title === 'Register'
                ) {
                  const hasNPN = state.status?.NPNVerified;
                  if (hasNPN) {
                    return (
                      <IConnectSignIn
                        key="integrity-connect-sign-in"
                        link={link.href}
                      />
                    );
                  } else {
                    return <NavSectionLink key={link.title} item={link} />;
                  }
                } else {
                  return <NavSectionLink key={link.title} item={link} />;
                }
              })}
            </List>
          </Collapse>
        </React.Fragment>
      );
    } else if (!hideFromBilling && item.to && canView && !hideFromStaff) {
      return (
        <ListItem
          ref={ref}
          dense
          button
          component={Link}
          to={item.to}
          onKeyDown={(event: React.KeyboardEvent<HTMLAnchorElement>) => {
            if (event.key === 'Tab') {
              event.stopPropagation();
            }
          }}
        >
          {item.startIcon || null}
          <ListItemText primary={item.title} className={classes.listItemText} />
        </ListItem>
      );
    } else if (!hideFromBilling && item.href && canView && !hideFromStaff) {
      return (
        <ListItem
          ref={ref}
          dense
          button
          component="a"
          href={item.href}
          target="_blank"
          rel="noreferrer"
          onKeyDown={stopPropagationForTab}
        >
          {item.startIcon || null}
          <ListItemText primary={item.title} className={classes.listItemText} />
        </ListItem>
      );
    } else if (item.shareLink) {
      const isMobile = detectMobile();
      const { shareLink } = item;
      const recAgtURL = shareLink?.url + RecAgtNo;

      // Use mailing function for desktop
      const handleMailTo = async () => {
        const subject = shareLink?.title ?? '';
        const bodyContent = `${shareLink?.text ?? ''}\n\n${recAgtURL}`;

        // Copy to clipboard
        try {
          await navigator.clipboard.writeText(recAgtURL);
          setOpenSnackbar(true);
        } catch (e) {
          console.warn('Clipboard failed:', e);
        }

        const mailTo = `mailto:?subject=${encodeURIComponent(
          subject,
        )}&body=${encodeURIComponent(bodyContent)}`;
        window.location.href = mailTo;
      };

      // Use share function for mobile
      const handleMobileShare = async () => {
        if (navigator.share) {
          try {
            await navigator.share({
              ...shareLink,
              url: recAgtURL,
            });
          } catch (error) {
            console.error('Error sharing:', error);
          }
        } else {
          // Fallback: Copy to clipboard
          navigator.clipboard.writeText(recAgtURL).then(() => {
            setOpenSnackbar(true);
          });
        }
      };
      return (
        <React.Fragment>
          <ListItemButton
            dense
            ref={ref}
            onKeyDown={stopPropagationForTab}
            onClick={isMobile ? handleMobileShare : handleMailTo}
          >
            {item.startIcon || null}
            <ListItemText
              primary={item.title}
              className={classes.listItemText}
            />
          </ListItemButton>
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={openSnackbar}
            autoHideDuration={3000}
            onClose={() => setOpenSnackbar(false)}
          >
            <Alert
              icon={<ContentCopy fontSize="small" />}
              variant="filled"
              severity="success"
              sx={{ width: '100%', color: 'white' }}
            >
              Link Copied to Clipboard!
            </Alert>
          </Snackbar>
        </React.Fragment>
      );
    } else {
      return null;
    }
  },
);
